import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import MISButton from "../../../../components/button";
import Layout from "../../../layout";
import { faAdd, faArrowLeft, faEye, faTrashAlt, faExclamationTriangle, faLink } from "@fortawesome/free-solid-svg-icons";
import PagedFilterTable from "../../../../components/table";
import { useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { createColumnHelper } from "@tanstack/react-table";
import IExtractClient from "../../../../models/ExtractClient";
import { ServiceManager } from "../../../../services/ServiceManager";
import { ExtractClientStatus, ExtractClientStatusLabels, MisType, Permissions } from "../../../../app/enums";
import CustomModal from "../../../../components/modal";
import { Dropdown } from "react-bootstrap";

import IconElement from "../../../../components/icon";
import { usePermissionCheck } from "../../../../app/common/helper/Permissions";
import MISTooltip from "../../../../components/tooltip";
import { PERMISSION_REQUIRED } from "../../../../app/constants";
import ExtractClientUpsert from "./extract-client-upsert";
import BeginDataMatchModal from "./begin-data-match-modal";
import DeleteExtractClientModal from "./delete-extract-client-modal";
import ChangeStatusModal from "./change-status-modal";
import { isDevelopmentBromcomPreviewConfiguration } from "../../../../services/DevelopmentBromcomPreview";

export default function ExtractClientSetup() {
    const [newExtractClient, setNewExtractClient] = useState(false);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [showStatusModal, setShowStatusModal] = useState(false);
    const [configurationId, setConfigurationId] = useState(0);
    const [pendingStatus, setPendingStatus] = useState<ExtractClientStatus | null>(null);
    const [showDataMatchModal, setShowDataMatchModal] = useState(false);
    const [selectedDataMatchMisConfigId, setSelectedDataMatchMisConfigId] = useState<number | null>(null);
    const [tableJobs, setTableJobs] = useState([]);
    const canEditEstablishments = usePermissionCheck(Permissions.CanEditEstablishments);

    const handleCloseDeleteModal = () => setShowDeleteModal(false);
    const handleCloseStatusModal = () => {
        setShowStatusModal(false);
        setPendingStatus(null);
        setConfigurationId(0);
    };

    const handleStatusSelect = (misConfigId: number, newStatus: ExtractClientStatus) => {
        setConfigurationId(misConfigId);
        setPendingStatus(newStatus);
        setShowStatusModal(true);
    };

    const handleCloseDataMatchModal = () => {
        setShowDataMatchModal(false);
        setSelectedDataMatchMisConfigId(null);
    };

    const { establishmentId } = useParams();
    let navigate = useNavigate();

    const columnHelper = createColumnHelper<IExtractClient>();
    const columns = [
        columnHelper.accessor('clientID', {
            header: () => <span>Client ID</span>,
            size: 40
        }),
        columnHelper.accessor('misType', {
            header: () => <span>MIS</span>,
            cell: ({ row }) => (
                <span>
                    {MisType[row.original.misType]?.toUpperCase()}
                    {isDevelopmentBromcomPreviewConfiguration(row.original.clientConfiguration.misConfigurationID) ? " (Preview)" : ""}
                </span>
            ),
            size: 25
        }),
        columnHelper.accessor('clientConfiguration.settingsJson', {
            header: () => <span>Username/ID</span>,
            cell: ({ row }) => {
                const { misType, clientConfiguration } = row.original;
                let value = "";
                try {
                    const settings = JSON.parse(clientConfiguration.settingsJson || "{}");
                    if (misType === MisType.Sims) {
                        value = settings.Username || "";
                    } else if (misType === MisType.Bromcom) {
                        value = settings.SchoolID || "";
                    } else if (misType === MisType.Arbor) {
                        value = settings.ApiCredentials.EstablishmentUrl || "";
                    }
                } catch {
                    value = "";
                }
                return <span>{value}</span>;
            },
            size: 20
        }),
        columnHelper.accessor('clientConfiguration.status', {
            header: () => <span>Status</span>,
            cell: ({ row }) => {
                const currentStatus = row.original.clientConfiguration.status ?? ExtractClientStatus.Paused;
                const misConfigId = row.original.clientConfiguration.misConfigurationID;
                
                return canEditEstablishments ? (
                    <Dropdown onSelect={(eventKey) => {
                        const newStatus = Number(eventKey) as ExtractClientStatus;
                        if (newStatus !== currentStatus) {
                            handleStatusSelect(misConfigId, newStatus);
                        }
                    }}>
                        <Dropdown.Toggle className="btn btn-light border d-flex justify-content-between align-items-center" size="sm" id={`status-dropdown-${misConfigId}`}>
                            {ExtractClientStatusLabels[currentStatus]}
                        </Dropdown.Toggle>
                        <Dropdown.Menu className="timer-dropdown extract-client-status-menu">
                            <Dropdown.Item eventKey={ExtractClientStatus.Paused} active={currentStatus === ExtractClientStatus.Paused}>
                                Paused
                            </Dropdown.Item>
                            <Dropdown.Item eventKey={ExtractClientStatus.Ingest} active={currentStatus === ExtractClientStatus.Ingest}>
                                Ingest
                            </Dropdown.Item>
                            <Dropdown.Item eventKey={ExtractClientStatus.Active} active={currentStatus === ExtractClientStatus.Active}>
                                Active
                            </Dropdown.Item>
                        </Dropdown.Menu>
                    </Dropdown>
                ) : (
                    <span>{ExtractClientStatusLabels[currentStatus]}</span>
                );
            },
            size: 15
        }),
        columnHelper.accessor('actions', {
            header: () => <span>Actions</span>,
            size: 14,
            cell: ({ row }) => (
                <div className="d-flex flex-row flex-nowrap align-items-center gap-3 extract-client-actions">
                    <FontAwesomeIcon
                        title="View"
                        className="cursor-pointer flex-shrink-0"
                        icon={faEye}
                        size="lg"
                        onClick={() => { viewExtractClient(row.original.clientConfiguration.misConfigurationID) }}
                    />
                    {
                        canEditEstablishments ?
                            <FontAwesomeIcon
                                title="Delete"
                                className="cursor-pointer flex-shrink-0"
                                icon={faTrashAlt}
                                size="lg"
                                onClick={() => { setShowDeleteModal(true); setConfigurationId(row.original.clientConfiguration.misConfigurationID) }}
                            />
                            :
                            <MISTooltip message={PERMISSION_REQUIRED} placement="top">
                                <span className="d-inline-flex flex-shrink-0">
                                    <FontAwesomeIcon
                                        icon={faTrashAlt}
                                        size="lg"
                                        className="disabled"
                                    />
                                </span>
                            </MISTooltip>
                    }
                </div>
            )
        })
    ];

    const addNewExtractClient = (isTrue: boolean) => {
        setNewExtractClient(isTrue);
    }

    const viewExtractClient = (configId: any) => {
        navigate(configId.toString())
    }

    async function fetchData(options: {
        pageIndex: number;
        pageSize: number;
        searchValue: string
    }) {
        const establishmentResponse = await ServiceManager.ConfigurationService.GetExtractClients(establishmentId);
        if (establishmentResponse == null) {
            ServiceManager.ToastService.showError("Unable to load extract clients. Check the API or your connection.");
            setTableJobs([]);
            return [];
        }
        setTableJobs(establishmentResponse);
        return establishmentResponse;
    }

    const clients = tableJobs as IExtractClient[];
    const showBeginDataMatch = clients.length >= 2;
    const dataMatchEligibleClients = clients.filter((c) => {
        const st = c.clientConfiguration.status ?? ExtractClientStatus.Paused;
        return st === ExtractClientStatus.Paused || st === ExtractClientStatus.Ingest;
    });

    const tableQueryKey = `extract-clients-view-${establishmentId}`;

    return (
        <>
            {
                newExtractClient === false
                    ? (
                        <Layout title="MIS Client"
                            button={
                                <div className="d-flex flex-wrap gap-2 justify-content-end">
                                    {showBeginDataMatch ? (
                                        canEditEstablishments ? (
                                            <MISButton
                                                text="Begin Data Match"
                                                className="btn btn-outline-primary"
                                                FnOnClick={() => {
                                                    setSelectedDataMatchMisConfigId(null);
                                                    setShowDataMatchModal(true);
                                                }}
                                                icon={<FontAwesomeIcon icon={faLink} />}
                                            />
                                        ) : (
                                            <MISTooltip message={PERMISSION_REQUIRED} placement="bottom">
                                                <div className="disabled">
                                                    <MISButton
                                                        text="Begin Data Match"
                                                        className="btn btn-outline-secondary"
                                                        FnOnClick={() => null}
                                                        disabled
                                                        icon={<FontAwesomeIcon icon={faLink} />}
                                                    />
                                                </div>
                                            </MISTooltip>
                                        )
                                    ) : null}
                                    {canEditEstablishments ? (
                                        <MISButton text="Add New Client" className="btn btn-primary crbc-bg-color" FnOnClick={() => addNewExtractClient(true)} icon={<FontAwesomeIcon icon={faAdd} />} />
                                    ) : (
                                        <MISTooltip message={PERMISSION_REQUIRED} placement="bottom">
                                            <div className="disabled">
                                                <MISButton text="Add New Client" className="btn btn-secondary" FnOnClick={() => null} disabled icon={<FontAwesomeIcon icon={faAdd} />} />
                                            </div>
                                        </MISTooltip>
                                    )}
                                </div>
                            }
                            backArrow={<FontAwesomeIcon icon={faArrowLeft} onClick={() => navigate(-1)} className="me-4 fs-5 cursor-pointer" />}
                        >
                            <div id="extractClientSetup">
                                <PagedFilterTable
                                    tableKey={`extract-clients-view-${establishmentId}`}
                                    columns={columns}
                                    data={tableJobs}
                                    usePagination={false}
                                    useSearch={false}
                                    pageRecords={10}
                                    fnFetchData={fetchData}
                                />
                            </div>
                        </Layout>
                    ) :
                    (
                        <ExtractClientUpsert upsertExtractClient={addNewExtractClient} />
                    )
            }
            <CustomModal isShow={showDeleteModal} handleClose={handleCloseDeleteModal} header={<IconElement iconType={faTrashAlt} headerClass="error-icon ms-6" color="#D92D20" />} size="md">
                <DeleteExtractClientModal
                    establishmentId={establishmentId}
                    configurationId={configurationId}
                    tableQueryKey={tableQueryKey}
                    onClose={handleCloseDeleteModal}
                />
            </CustomModal>
            <CustomModal isShow={showStatusModal} handleClose={handleCloseStatusModal} header={<IconElement iconType={faExclamationTriangle} headerClass="warning-icon ms-6" color="#F79009" />} size="md">
                <ChangeStatusModal
                    establishmentId={establishmentId}
                    configurationId={configurationId}
                    pendingStatus={pendingStatus}
                    tableQueryKey={tableQueryKey}
                    onClose={handleCloseStatusModal}
                />
            </CustomModal>
            <CustomModal isShow={showDataMatchModal} handleClose={handleCloseDataMatchModal} header={<IconElement iconType={faLink} headerClass="warning-icon ms-6" color="#1570EF" />} size="md">
                <p className="heading text-center mt-2">Begin Data Match</p>
                <BeginDataMatchModal
                    establishmentId={establishmentId}
                    selectedMisConfigId={selectedDataMatchMisConfigId}
                    onSelectedMisConfigIdChange={setSelectedDataMatchMisConfigId}
                    eligibleClients={dataMatchEligibleClients}
                    onClose={handleCloseDataMatchModal}
                />
            </CustomModal>
        </>
    );
}